import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDeckModal } from '../../contexts/DeckModalContext';
import {
  getStoredFundDeckToken,
  submitFundDeckRequest,
  validateStoredDeckSession,
} from '../../lib/submitFundDeckRequest';
import { userMessageFromCallableError } from '../../lib/firebaseCallableErrors';
import { FUND_DECK_PHONE_ERROR, isValidFundDeckPhone } from '../../lib/fundDeckPhone';
import '../../styles/kyro-design.css';
import './DeckRequestModal.css';

const DeckRequestModal: React.FC = () => {
  const { isOpen, closeDeckModal } = useDeckModal();
  const navigate = useNavigate();
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionActive, setSessionActive] = useState(false);
  const [deckError, setDeckError] = useState('');
  const [deckLoading, setDeckLoading] = useState(false);
  const [deckFields, setDeckFields] = useState({ name: '', org: '', email: '', phone: '' });
  const modalCardRef = useRef<HTMLDivElement>(null);
  const previousActiveRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCheckingSession(true);
      setSessionActive(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setCheckingSession(true);
      const ok = await validateStoredDeckSession();
      if (!cancelled) {
        setSessionActive(ok);
        setCheckingSession(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    previousActiveRef.current = document.activeElement as HTMLElement | null;
    const firstFocusable = modalCardRef.current?.querySelector<HTMLElement>(
      'button[aria-label="Close"], input, .deck-modal-download-btn, .deck-modal-submit'
    );
    firstFocusable?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDeckModal();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousActiveRef.current?.focus?.();
    };
  }, [isOpen, closeDeckModal, checkingSession, sessionActive]);

  const submitDeck = async () => {
    setDeckError('');
    const name = deckFields.name.trim();
    const phone = deckFields.phone.trim();
    const email = deckFields.email.trim();
    if (!name || !phone || !email) {
      setDeckError('Name, email and phone number are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setDeckError('Please enter a valid email address.');
      return;
    }
    if (!isValidFundDeckPhone(phone)) {
      setDeckError(FUND_DECK_PHONE_ERROR);
      return;
    }
    setDeckLoading(true);
    try {
      const token = await submitFundDeckRequest({
        name,
        email,
        phone,
        organisation: deckFields.org.trim() || null,
      });
      setSessionActive(true);
      setDeckFields({ name: '', org: '', email: '', phone: '' });
      closeDeckModal();
      navigate(`/fund-deck?t=${encodeURIComponent(token)}`);
    } catch (err) {
      console.error(err);
      setDeckError(userMessageFromCallableError(err));
    } finally {
      setDeckLoading(false);
    }
  };

  const goToDeckFromStored = () => {
    const t = getStoredFundDeckToken();
    if (!t) {
      setSessionActive(false);
      return;
    }
    closeDeckModal();
    navigate(`/fund-deck?t=${encodeURIComponent(t)}`);
  };

  const handleClose = () => {
    closeDeckModal();
    setDeckError('');
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="deck-modal-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-label="Fund deck access">
      <div className="deck-modal-card" ref={modalCardRef} onClick={(e) => e.stopPropagation()}>
        <button className="deck-modal-close" onClick={handleClose} aria-label="Close" type="button">
          ×
        </button>
        <div className="deck-modal-inner">
          <div className="eyebrow" style={{ color: 'var(--blue)' }}>
            Fund deck
          </div>
          <h2 className="deck-modal-title">
            This isn&apos;t just a fund.<br />
            <span className="deck-modal-title-dim">It&apos;s a partnership.</span>
          </h2>
          <p className="deck-modal-desc">
            The full deck — terms, strategy and track record — for eligible investors.
          </p>

          {checkingSession ? (
            <div className="deck-modal-success" style={{ textAlign: 'center' }}>
              …
            </div>
          ) : sessionActive ? (
            <div className="deck-modal-success" role="status">
              <p style={{ marginBottom: '1rem', fontSize: '0.88rem', lineHeight: 1.55 }}>
                You already have access in this browser.
              </p>
              <button type="button" className="deck-modal-download-btn" onClick={goToDeckFromStored}>
                View fund deck
              </button>
            </div>
          ) : (
            <>
              <div className="deck-modal-form">
                <input
                  className="deck-modal-input"
                  aria-label="Your name"
                  placeholder="Your Name *"
                  value={deckFields.name}
                  onChange={(e) => setDeckFields({ ...deckFields, name: e.target.value })}
                  required
                />
                <input
                  className="deck-modal-input"
                  type="email"
                  aria-label="Email address"
                  placeholder="Email Address *"
                  value={deckFields.email}
                  onChange={(e) => setDeckFields({ ...deckFields, email: e.target.value })}
                  required
                />
                <input
                  className="deck-modal-input"
                  type="tel"
                  aria-label="Phone number"
                  placeholder="Phone Number *"
                  value={deckFields.phone}
                  onChange={(e) => setDeckFields({ ...deckFields, phone: e.target.value })}
                  required
                />
                <input
                  className="deck-modal-input"
                  aria-label="Referred By (Distributor Name) / Organization (optional)"
                  placeholder="Referred By (Distributor Name) / Organization"
                  value={deckFields.org}
                  onChange={(e) => setDeckFields({ ...deckFields, org: e.target.value })}
                />
                {deckError && (
                  <div className="deck-modal-error" role="alert" aria-live="assertive">
                    {deckError}
                  </div>
                )}
                <button className="deck-modal-submit" type="button" onClick={submitDeck} disabled={deckLoading}>
                  {deckLoading ? 'Opening deck…' : 'View fund deck →'}
                </button>
              </div>
              <div className="deck-modal-or">or reach us directly</div>
              <div className="deck-modal-direct">
                <a href="mailto:compliance@kyrocapital.in">✉ compliance@kyrocapital.in</a>
                <a href="tel:+917611111102">✆ 76 111 111 02</a>
              </div>
            </>
          )}

          <div className="deck-modal-disc">
            Kyro India Opportunities Fund – I is a SEBI-registered Category II AIF (Reg. No. IN/AIF2/26-27/2191). For eligible
            investors under SEBI (AIF) Regulations, 2012. For Private Circulation Only.
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeckRequestModal;
